import React from "react";

import {
  Box,
  BoxPanel,
  IconButton,
  PieChart,
  PieChartDialog,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TruncateTooltip,
  CircularProgress,
} from "@tiny/components";
import { useGetCustomerIspBreakdownQuery } from "@tiny/api";
import { useRefreshRegister } from "@tiny/components/src/RefreshProvider";
import { useColorAccessor } from "../AudienceDetails/AudienceDetails.hooks";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import {
  formatNumber,
  formatPercent,
} from "@tiny/components/src/utils/formatUtils";
import Expand2 from "@tiny/icons/generated/Expand2";

function SubscribersByISPStats() {
  const { mdUp } = useBreakpoints();
  const colorAccessor = useColorAccessor();

  const { data = [], refetch, isLoading } = useGetCustomerIspBreakdownQuery();

  useRefreshRegister(async () => {
    await refetch();
  });

  const [dialogOpen, setDialogOpen] = React.useState(false);

  const chartData = React.useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        color: colorAccessor(item, index),
      })),
    [data, colorAccessor]
  );

  const preview = chartData.slice(0, 4);

  return (
    <>
      <BoxPanel
        title="Subscribers by ISP"
        contentWithPadding
        headerNoBorder={!mdUp}
        actions={
          !isLoading && (
            <IconButton onClick={() => setDialogOpen(true)} size="small">
              <Expand2 />
            </IconButton>
          )
        }
      >
        {isLoading ? (
          <Box
            width="100%"
            height="220px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
            <Box flex="1" order={{ xs: 2, md: 1 }}>
              <Table size="small" sidePadding={0} layoutFixed hideLastRowBorder>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="label3" color="grey" tone={700}>
                        ISP
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="80px">
                      <Typography variant="label3" color="grey" tone={700}>
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell align="right" width="92px">
                      <Typography variant="label3" color="grey" tone={700}>
                        Percent
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((item) => (
                    <TableRow key={item.isp}>
                      <TableCell>
                        <TruncateTooltip>
                          <Typography
                            variant="body2"
                            color="grey"
                            tone={900}
                            noWrap
                            display="block"
                            my={1}
                            bullet
                            bulletColor={item.color}
                          >
                            {item.isp}
                          </Typography>
                        </TruncateTooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="grey"
                          tone={900}
                          fontWeight="bold"
                        >
                          {formatNumber(item.count)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color="grey"
                          tone={900}
                          fontWeight="bold"
                        >
                          {formatPercent(item.percentage)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box
              flex="0 0 178px"
              display="flex"
              justifyContent={{ xs: "center", md: "flex-end" }}
              alignItems="center"
              order={{ xs: 1, md: 2 }}
            >
              <PieChart
                width={150}
                data={chartData}
                valueAccessor={(item) => item.count}
                colorAccessor={(item) => item.color ?? ""}
              />
            </Box>
          </Box>
        )}
      </BoxPanel>
      {!isLoading && (
        <PieChartDialog
          title="ISP"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          data={chartData}
          valueAccessor={(item) => item.count}
          labelHeader="ISP"
          valueHeader="Percent"
          colorAccessor={(item) => item.color}
          labelAccessor={(item) =>
            `${item.isp}\u2002•\u2002${formatNumber(item.count)}`
          }
        />
      )}
    </>
  );
}

export default SubscribersByISPStats;
